import { create } from 'zustand';
import { GameState, GameAction } from '../../shared/types';

interface GameStore {
  // 接続状態
  isConnected: boolean;
  playerId: string | null;
  playerName: string;
  gameId: string | null;
  
  // ゲーム状態
  gameState: GameState | null;
  
  // WebSocket
  ws: WebSocket | null;
  
  // UI状態
  selectedAction: 'road' | 'settlement' | 'city' | null;
  isLoading: boolean;
  error: string | null;
  
  // アクション
  setPlayerName: (name: string) => void;
  createGame: (mode: 'standard' | 'extended') => Promise<void>;
  joinGame: (gameId: string) => Promise<void>;
  connect: (gameId: string) => void;
  disconnect: () => void;
  sendAction: (action: GameAction) => void;
  
  // UI アクション
  setSelectedAction: (action: 'road' | 'settlement' | 'city' | null) => void;
  clearError: () => void;
}

const ENV = (import.meta as unknown as { env: { DEV: boolean; VITE_API_BASE?: string } }).env;
const API_BASE = ENV.DEV
  ? 'http://localhost:8787'
  : (ENV.VITE_API_BASE || 'https://catan-web.chao200222.workers.dev');

export const useGameStore = create<GameStore>((set, get) => ({
  // 初期状態
  isConnected: false,
  playerId: null,
  playerName: '',
  gameId: null,
  gameState: null,
  ws: null,
  selectedAction: null,
  isLoading: false,
  error: null,
  
  setPlayerName: (name) => set({ playerName: name }),
  
  createGame: async (mode) => {
    set({ isLoading: true, error: null });
    try {
      const response = await fetch(`${API_BASE}/api/games`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode }),
      });
      
      if (!response.ok) throw new Error('Failed to create game');
      
      const data = await response.json() as { gameId: string };
      set({ gameId: data.gameId });
      
      // WebSocket接続
      get().connect(data.gameId);
    } catch (error) {
      set({ error: (error as Error).message });
    } finally {
      set({ isLoading: false });
    }
  },
  
  joinGame: async (gameId) => {
    set({ isLoading: true, error: null, gameId });
    try {
      // ゲームの存在確認
      const response = await fetch(`${API_BASE}/api/games/${gameId}`);
      if (!response.ok) throw new Error('Game not found');
      
      // WebSocket接続
      get().connect(gameId);
    } catch (error) {
      set({ error: (error as Error).message, gameId: null });
    } finally {
      set({ isLoading: false });
    }
  },
  
  connect: (gameId) => {
    const apiBase = API_BASE || window.location.origin;
    const wsBase = apiBase.replace(/^http/, 'ws');
    const wsUrl = `${wsBase}/api/games/${gameId}/ws`;
    
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      set({ isConnected: true });
      
      // プレイヤーIDを生成して参加
      const playerId = crypto.randomUUID();
      const { playerName } = get();
      
      set({ playerId });
      
      ws.send(JSON.stringify({
        type: 'action',
        payload: {
          type: 'join',
          playerId,
          playerName: playerName || `Player ${playerId.slice(0, 4)}`,
        },
      }));
    };
    
    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as { type: string; payload: GameState | { message: string } };
        
        if (message.type === 'state_update') {
          set({ gameState: message.payload as GameState });
        } else if (message.type === 'error') {
          set({ error: (message.payload as { message: string }).message });
        }
      } catch (e) {
        console.error('Failed to parse message:', e);
      }
    };
    
    ws.onclose = () => {
      set({ isConnected: false });
    };
    
    ws.onerror = () => {
      set({ error: 'Connection error' });
    };
    
    set({ ws });
  },
  
  disconnect: () => {
    const { ws } = get();
    if (ws) {
      ws.close();
    }
    set({
      ws: null,
      isConnected: false,
      gameState: null,
      gameId: null,
    });
  },
  
  sendAction: (action) => {
    const { ws, playerId } = get();
    if (!ws || !playerId) return;
    
    ws.send(JSON.stringify({
      type: 'action',
      payload: { ...action, playerId },
    }));
  },
  
  setSelectedAction: (action) => set({ selectedAction: action }),
  
  clearError: () => set({ error: null }),
}));

// ヘルパーフック
export function useCurrentPlayer() {
  const { gameState, playerId } = useGameStore();
  if (!gameState || !playerId) return null;
  return gameState.players.find(p => p.id === playerId);
}

export function useIsMyTurn() {
  const { gameState, playerId } = useGameStore();
  if (!gameState || !playerId) return false;
  return gameState.players[gameState.currentPlayerIndex]?.id === playerId;
}
